import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MatchmakerLayout } from "@/components/matchmaker/MatchmakerLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ProfileFilters } from "@/components/matchmaker/ProfileFilters";
import { ProfileCard } from "@/components/matchmaker/ProfileCard";
import { ProfileDetailModal } from "@/components/matchmaker/ProfileDetailModal";
import { useMatchmakerProfiles, ProfileFilters as FiltersType } from "@/hooks/useMatchmakerProfiles";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ChevronLeft, ChevronRight, Info, UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { anonymizeProfile } from "@/utils/anonymization";
import { useUserRole } from "@/hooks/useUserRole";

export default function MatchmakerProfiles() {
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<FiltersType>({
    search: "",
    gender: undefined,
    ageMin: 18,
    ageMax: 60,
    locations: [],
    religiosityLevels: [],
    educationLevels: [],
    ethnicities: [],
    maritalStatuses: [],
    citizenshipStatuses: [],
    sects: [],
    activeOnly: false,
    status: "all",
  });
  const [selectedProfile, setSelectedProfile] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { profiles, loading, error, totalCount, totalPages, currentPage } =
    useMatchmakerProfiles(filters, page);

  // Apply anonymization if admin
  const displayProfiles = isAdmin ? profiles.map(anonymizeProfile) : profiles;

  const handleViewDetails = (profile: any) => {
    setSelectedProfile(profile);
    setModalOpen(true);
  };

  const handleCreateMatch = async (profileId: string) => {
    // For now, navigate to smart matcher where they can select a second profile
    // In the future, this could open a profile selector dialog
    navigate(`/matchmaker/smart-matcher`);
  };

  const handleDeleteClick = (profileId: string) => {
    setProfileToDelete(profileId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!profileToDelete) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .delete()
        .eq("id", profileToDelete);

      if (error) throw error;

      toast({
        title: "Profile deleted",
        description: "The profile has been successfully removed.",
      });

      // Refresh the profiles list by forcing a re-render
      setPage(0);
      setTimeout(() => setPage(page), 0);
    } catch (error: any) {
      console.error("matchmaker.delete_profile_failed");
      toast({
        title: "Error",
        description: error.message || "Failed to delete profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setProfileToDelete(null);
    }
  };

  const Layout = isAdmin ? AdminLayout : MatchmakerLayout;
  const pageTitle = isAdmin ? "All Profiles (Anonymized)" : "All Profiles";
  const pageDescription = isAdmin
    ? "Search and filter profiles to create matches (data anonymized for privacy)"
    : "Search and filter profiles to create matches";

  return (
    <Layout title={pageTitle}>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Welcome Banner */}
        <div className="mb-8 rounded-2xl bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 p-6 border border-primary/20 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <span className="text-3xl">💝</span>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                {isAdmin ? "Profile Management Hub" : "Let's Create Amazing Matches!"}
              </h2>
            </div>
            {!isAdmin && (
              <Button
                onClick={() => navigate("/matchmaker/create-profile")}
                className="gap-2"
              >
                <UserPlus className="h-4 w-4" />
                Create Profile
              </Button>
            )}
          </div>
          <p className="text-foreground/70">
            {pageDescription}
          </p>
          {!isAdmin && (
            <div className="mt-3">
              <Link
                to="/matchmaker/guide"
                className="text-sm text-primary underline-offset-4 hover:underline"
              >
                New here? Read the matchmaker onboarding guide →
              </Link>
            </div>
          )}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <Info className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <ProfileFilters
              filters={filters}
              onFiltersChange={(newFilters) => {
                setFilters(newFilters);
                setPage(1); // Reset to first page on filter change
              }}
              resultsCount={totalCount}
            />
          </div>

          {/* Profiles Grid */}
          <div className="lg:col-span-3 space-y-6">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-64 w-full" />
                ))}
              </div>
            ) : displayProfiles.length === 0 ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>No profiles found</AlertTitle>
                <AlertDescription>
                  Try adjusting your filters to see more results.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="space-y-4">
                  {displayProfiles.map((profile) => (
                    <ProfileCard
                      key={profile.id}
                      profile={profile}
                      onViewDetails={handleViewDetails}
                      onDelete={handleDeleteClick}
                      showFindMatches={true}
                      isAdmin={isAdmin}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between border-t pt-6">
                    <p className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Profile Detail Modal */}
      <ProfileDetailModal
        profileId={selectedProfile?.id || null}
        profile={selectedProfile}
        open={modalOpen}
        onOpenChange={setModalOpen}
        onCreateMatch={handleCreateMatch}
        anonymized={isAdmin}
        isAdmin={isAdmin}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the profile and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete Profile"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
